import React from 'react';
import { ChevronLeft, ChevronRight, Trash2, Plus } from 'lucide-react';
import { Card, Field, TextField, CssColorField, SaveButton, useSection } from '../AdminUI';
import { Switch } from '../../ui/switch';
import { useSite } from '../../../context/SiteContext';

const DEFAULT_NAV_ITEMS = [
  { id: 'home', label: 'HOME', link: '/' },
  { id: 'b2b', label: 'GET B2B', link: '#exchanges' },
  { id: 'b2c', label: 'GET B2C', link: '#exchanges' },
  { id: 'getid', label: 'GET ID', link: '#get-id' },
  { id: 'whitelabel', label: 'WHITE LABEL', link: '#whitelabel' },
  { id: 'lineaccess', label: 'LINE ACCESS', link: '#lineaccess' },
  { id: 'joinplatform', label: 'JOIN PLATFORM', link: '#joinplatform' },
  { id: 'services', label: 'BUSINESS SERVICES', link: '#services' },
  { id: 'reviews', label: 'REVIEWS', link: '#reviews' },
  { id: 'casestudies', label: 'CASE STUDIES', link: '#cases' },
  { id: 'about', label: 'ABOUT', link: '#about' },
  { id: 'contact', label: 'CONTACT', link: '#contact' },
];

export default function HeaderSection() {
  const { data, updateSection } = useSite();
  const [local, setLocal, save] = useSection('header');
  const currentBg = local.bgColor || data?.branding?.headerBg || '#777544';
  const set = (k, v) => setLocal({ ...local, [k]: v });

  // Navigation items management
  const navItems = data.navigation && data.navigation.length > 0 ? data.navigation : DEFAULT_NAV_ITEMS;
  const [workingNav, setWorkingNav] = React.useState(navItems);

  const moveLeft = (idx) => {
    if (idx === 0) return;
    const next = [...workingNav];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    setWorkingNav(next);
  };
  const moveRight = (idx) => {
    if (idx === workingNav.length - 1) return;
    const next = [...workingNav];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    setWorkingNav(next);
  };
  const removeItem = (idx) => setWorkingNav(workingNav.filter((_, i) => i !== idx));
  const resetNav = () => setWorkingNav(DEFAULT_NAV_ITEMS);

  const handleSave = async () => {
    // Save header settings first
    await save();
    // Also persist navigation order into site data
    updateSection('navigation', workingNav);
    window.dispatchEvent(new CustomEvent('site-flags-updated'));
  };

  return (
    <Card title="Header & Navigation" desc="Header background, search, clock, button labels, and navigation item order." footer={<SaveButton onClick={handleSave} />}>
      {/* ── Navigation Item Order ─────────────────────────────────── */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold text-zinc-300">Navigation Item Order</label>
          <button onClick={resetNav} className="text-xs text-zinc-500 hover:text-white border border-zinc-700 rounded px-2 py-1 transition">Reset Default</button>
        </div>
        <div className="space-y-1.5 max-h-[340px] overflow-y-auto pr-1 no-scrollbar">
          {workingNav.map((item, idx) => (
            <div key={item.id + idx} className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2">
              <span className="text-zinc-500 text-xs w-5 shrink-0">{idx + 1}.</span>
              <span className="flex-1 text-xs font-semibold text-white truncate">{item.label}</span>
              <span className="text-zinc-600 text-[10px] truncate max-w-[80px] hidden sm:block">{item.link}</span>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => moveLeft(idx)}
                  disabled={idx === 0}
                  className="w-7 h-7 flex items-center justify-center rounded bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed transition text-white"
                  aria-label="Move left"
                  title="Move left"
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  onClick={() => moveRight(idx)}
                  disabled={idx === workingNav.length - 1}
                  className="w-7 h-7 flex items-center justify-center rounded bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed transition text-white"
                  aria-label="Move right"
                  title="Move right"
                >
                  <ChevronRight size={14} />
                </button>
                <button
                  onClick={() => removeItem(idx)}
                  className="w-7 h-7 flex items-center justify-center rounded bg-red-900/30 hover:bg-red-900/60 border border-red-900/40 transition text-red-400"
                  aria-label="Remove item"
                  title="Remove"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-zinc-600 mt-2">← → moves items left/right. Save to persist order.</p>
      </div>

      {/* ── Other Header Settings ─────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Header Background (CSS / Solid / Gradient)" hint="Solid color (e.g. #777544) or CSS linear-gradient">
          <CssColorField
            value={currentBg}
            onChange={(v) => set('bgColor', v)}
            placeholder="linear-gradient(...) or #777544"
          />
        </Field>
        <Field label="Navigation Bar Background" hint="Solid color (e.g. #3e3d26) or gradient for top nav row">
          <CssColorField
            value={local.navBg || '#3e3d26'}
            onChange={(v) => set('navBg', v)}
            placeholder="#3e3d26 or linear-gradient(...)"
          />
        </Field>
        <Field label="Search Placeholder">
          <TextField value={local.searchPlaceholder} onChange={(v) => set('searchPlaceholder', v)} />
        </Field>
        <div>
          <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Show Clock</label>
          <div className="h-10 flex items-center gap-3">
            <Switch checked={local.showClock} onCheckedChange={(v) => set('showClock', v)} />
            <span className="text-sm text-zinc-400">{local.showClock ? 'Visible' : 'Hidden'}</span>
          </div>
        </div>
        <Field label="Login Button Label">
          <TextField value={local.loginLabel} onChange={(v) => set('loginLabel', v)} />
        </Field>
        <Field label="Sign Up Button Label">
          <TextField value={local.registerLabel} onChange={(v) => set('registerLabel', v)} />
        </Field>
        <div>
          <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Show Login Button</label>
          <div className="h-10 flex items-center gap-3">
            <Switch checked={local.showLogin !== false} onCheckedChange={(v) => set('showLogin', v)} data-testid="header-show-login-toggle" />
            <span className="text-sm text-zinc-400">{local.showLogin !== false ? 'Visible' : 'Hidden'}</span>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Show Sign Up Button</label>
          <div className="h-10 flex items-center gap-3">
            <Switch checked={local.showSignup !== false} onCheckedChange={(v) => set('showSignup', v)} data-testid="header-show-signup-toggle" />
            <span className="text-sm text-zinc-400">{local.showSignup !== false ? 'Visible' : 'Hidden'}</span>
          </div>
        </div>

        {/* ─── B2B / B2C / ID visibility ─── */}
        <div>
          <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Show B2B Button</label>
          <div className="h-10 flex items-center gap-3">
            <Switch checked={local.showGetB2B !== false} onCheckedChange={(v) => set('showGetB2B', v)} data-testid="header-show-getb2b-toggle" />
            <span className="text-sm text-zinc-400">{local.showGetB2B !== false ? 'Visible' : 'Hidden'}</span>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Show B2C Button</label>
          <div className="h-10 flex items-center gap-3">
            <Switch checked={local.showGetB2C !== false} onCheckedChange={(v) => set('showGetB2C', v)} data-testid="header-show-getb2c-toggle" />
            <span className="text-sm text-zinc-400">{local.showGetB2C !== false ? 'Visible' : 'Hidden'}</span>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Show GET ID Button</label>
          <div className="h-10 flex items-center gap-3">
            <Switch checked={local.showGetId !== false} onCheckedChange={(v) => set('showGetId', v)} data-testid="header-show-getid-toggle" />
            <span className="text-sm text-zinc-400">{local.showGetId !== false ? 'Visible' : 'Hidden'}</span>
          </div>
        </div>
      </div>
      <div className="mt-4">
        <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Header Background Preview</label>
        <div className="h-12 rounded-lg border border-zinc-800 shadow-inner" style={{ background: currentBg }} />
      </div>
    </Card>
  );
}
